import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
} from '@/features/legends/editor/analyzer-errors'
import type { AnswerOptions } from '@/features/legends/editor/entities'
import { Types } from '@/features/legends/editor/ryno-types'
import type { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import {
  type EffectDescriptor,
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { BoolQuestion } from '@/features/legends/questions/questions'
import { validateObject } from '@/utils/validx-util'

/**
 * A yes/no question, with an effect for each answer.
 */
export class BoolQuestionEditorViewModel extends QuestionEditorViewModelBase<BoolQuestion> {
  private readonly _schema: IValidationSchema<BoolQuestionEditorViewModel> = {
    ...QuestionEditorViewModelBase.sharedSchema,
    question: [required('Question is required')],
    yes: [validateObject('There are invalid effects')],
    no: [validateObject('There are invalid effects')],
  }

  readonly type = 'Bool'

  /**
   * Runs when the answer is yes.
   */
  readonly yes: EffectEditorViewModel

  /**
   * Runs when the answer is no.
   */
  readonly no: EffectEditorViewModel

  @observable accessor question = 'New Yes/No Question'
  @observable accessor description = ''

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.yes = this.createEffect()
    this.no = this.createEffect()
    this.lifecycle.manage(this.yes)
    this.lifecycle.manage(this.no)
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  get valueType() {
    return Types.boolean
  }

  /** @inheritDoc */
  get displayText() {
    return this.question
  }

  /** @inheritDoc */
  @computed
  get answerOptions(): AnswerOptions {
    return {
      type: 'select',
      options: [
        { id: 'yes', value: true, text: 'Yes' },
        { id: 'no', value: false, text: 'No' },
      ],
    }
  }

  /** @inheritDoc */
  @computed
  get effectDescriptors(): EffectDescriptor[] {
    return [
      { id: 'yes', title: 'Yes', effect: this.yes },
      { id: 'no', title: 'No', effect: this.no },
    ]
  }

  /** @inheritDoc */
  @action
  hydrate(question: BoolQuestion) {
    this._hydrateShared(question)
    this.question = question.question ?? this.question
    this.description = question.description ?? ''
    this.yes.hydrate(question.yes)
    this.no.hydrate(question.no)
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor.inner?.yes,
      'yes',
      'There are invalid effects.',
    )
    addGenericErrorForProperty(
      this,
      descriptor.inner?.no,
      'no',
      'There are invalid effects.',
    )
    super.receiveErrors(descriptor)
  }

  /** @inheritDoc */
  protected _serialize(): BoolQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      question: this.question,
      description: this.description,
      yes: this.yes.toJSON(),
      no: this.no.toJSON(),
    }
  }
}
